import React, { useEffect } from "react";

import { variaveisDaRegiao } from "./estiloDaRegiao";

import "../styles/telaCheia.css";

const ROTULO_ENTRAR = "Ver em tela cheia";
const ROTULO_SAIR = "Sair da tela cheia";

/**
 * Setas para os cantos (entrar) ou para o centro (sair). Desenhadas inline em
 * vez de virarem asset: são dois traçados de 10 linhas, e um `.svg` importado
 * seria um arquivo a mais para o jest do CRA substituir por vazio.
 */
function IconeTelaCheia({ ativo }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ativo ? (
        <>
          <path d="M8 3v3a2 2 0 0 1-2 2H3" />
          <path d="M16 3v3a2 2 0 0 0 2 2h3" />
          <path d="M8 21v-3a2 2 0 0 0-2-2H3" />
          <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
        </>
      ) : (
        <>
          <path d="M8 3H5a2 2 0 0 0-2 2v3" />
          <path d="M16 3h3a2 2 0 0 1 2 2v3" />
          <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
          <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
        </>
      )}
    </svg>
  );
}

/**
 * `Esc` só sai da tela cheia quando NÃO há diálogo aberto.
 *
 * Em tela cheia o aluno continua abrindo os itens, e cada item abre um modal da
 * página hospedeira. Sem esta guarda, um `Esc` fecharia o modal E a tela cheia na
 * mesma tecla — o aluno pediu para fechar o conteúdo e perderia o modo junto.
 * Vale também para os diálogos da própria trilha: a cerimônia de card e a
 * apresentação do mascote têm `role="dialog"`/`aria-modal`.
 *
 * PRESENÇA NO DOM NÃO BASTA, e é o detalhe que faz esta função existir em vez de
 * um `querySelector` de uma linha: medido na página `Do` do AVA, ela mantém OITO
 * modais montados o tempo todo (apagar anotação, estatísticas, compartilhar,
 * correção do Literarte, ...), todos com `role="dialog"`, sete deles em
 * `display: none` — é assim que o `.modal` do Bootstrap fica fechado. Testar só a
 * existência do seletor dava "diálogo aberto" para sempre, e o `Esc` nunca teria
 * funcionado no AVA. Passando em teste de unidade, onde não existe modal do host.
 *
 * Por que `getComputedStyle` e não `getClientRects().length`, que seria a medida
 * mais direta de "está na tela": o jsdom não faz layout, então lá TODO elemento
 * devolve zero retângulos — a guarda ficaria intestável e sempre falsa em teste,
 * exatamente o oposto do bug que ela corrige. `display`/`visibility` o jsdom
 * responde, e é o mecanismo que o Bootstrap usa de verdade.
 *
 * `offsetParent` não serviria de jeito nenhum: modal é `position: fixed`, e para
 * elemento fixo ele é null mesmo com o modal aberto na cara do aluno.
 *
 * Consulta o DOM em vez de receber um `temDialogo` por prop de propósito: os
 * modais que importam são do HOST, e o núcleo não pode saber quais são. `role` e
 * `aria-modal` são contrato de acessibilidade, não de um framework.
 */
function temDialogoAberto() {
  const dialogos = document.querySelectorAll(
    '[role="dialog"], [aria-modal="true"]'
  );
  return Array.prototype.some.call(dialogos, el => {
    const estilo = window.getComputedStyle(el);
    return estilo.display !== "none" && estilo.visibility !== "hidden";
  });
}

/**
 * Modo TELA CHEIA da trilha — envolve a experiência INTEIRA (abertura, seleção
 * de explorador, apresentação do mascote, tabuleiro e encerramento) e oferece o
 * botão que liga e desliga o modo.
 *
 * Componente de APRESENTAÇÃO: recebe `ativo` e devolve o pedido de troca. Quem
 * guarda a preferência é quem o usa — no AVA, `ava/TrilhaDeAtividade`.
 *
 * NÃO usa portal, ao contrário de `CerimoniaCard`. O portal existe lá porque
 * `position: fixed` dentro de um ancestral com `transform` passa a se posicionar
 * contra o ancestral, e não contra a viewport. Aqui isso foi MEDIDO na página
 * `Do` do AVA: nenhum dos 15 ancestrais cria containing block, e uma sonda
 * `fixed; inset: 0` no lugar exato desta moldura devolveu 1366x900 em (0,0).
 * Ficar no lugar é o que preserva o estado da árvore ao alternar o modo — com
 * portal, mudar o container remonta os filhos, e o aluno que ligasse a tela
 * cheia no meio do onboarding voltaria para a abertura, perdendo o explorador
 * que tinha acabado de escolher.
 *
 * @param {boolean}  ativo       modo ligado
 * @param {Function} onAlternar  recebe o novo valor (true/false)
 * @param {object}   [regiao]    descritor da região, para a paleta e o cenário
 *                               valerem nas TRÊS telas que a moldura envolve —
 *                               `Onboarding` e `Encerramento` não recebem essas
 *                               custom properties de mais ninguém. Custom property
 *                               é herdada, então declarar aqui basta (é o mesmo
 *                               motivo pelo qual a moldura já estava na lista de
 *                               lugares onde tokens podem morar — ver a regra 2 do
 *                               README).
 * @param {Object}   children    a trilha inteira
 */
function ModoTelaCheia({ ativo, onAlternar, regiao, children }) {
  useEffect(() => {
    if (!ativo) return undefined;

    function aoTeclar(evento) {
      if (evento.key !== "Escape" && evento.key !== "Esc") return;
      if (temDialogoAberto()) return;
      onAlternar(false);
    }

    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [ativo, onAlternar]);

  // Trava a rolagem da PÁGINA enquanto o modo está ligado. Sem isto o conteúdo
  // do AVA rola por baixo do overlay quando a roda do mouse cai numa área que
  // não rola (encadeamento de scroll), e o aluno vê a barra lateral do navegador
  // andar sem nada acontecer na tela. Guarda e restaura o valor anterior em vez
  // de zerar: a página pode ter um inline style próprio.
  useEffect(() => {
    if (!ativo) return undefined;

    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [ativo]);

  return (
    <div
      className={
        ativo
          ? "trilha-ludica__moldura trilha-ludica__overlay"
          : "trilha-ludica__moldura"
      }
      style={variaveisDaRegiao(regiao)}
    >
      <button
        type="button"
        className="trilha-ludica__tela-cheia"
        onClick={() => onAlternar(!ativo)}
        // `aria-pressed` e não dois botões: é um interruptor, e o leitor de tela
        // anuncia o estado sem o rótulo ter de mudar de significado.
        aria-pressed={ativo}
        title={ativo ? ROTULO_SAIR : ROTULO_ENTRAR}
      >
        <IconeTelaCheia ativo={ativo} />
        <span className="trilha-ludica__so-leitor">
          {ativo ? ROTULO_SAIR : ROTULO_ENTRAR}
        </span>
      </button>

      {children}
    </div>
  );
}

export default ModoTelaCheia;
